<template>
  <div class="active-chat customer-chat-active">
    <!-- Cabeçalho -->
    <div class="chat-navbar">
      <header class="chat-header customer-chat-header">
        <div class="d-flex align-items-center overflow-hidden">
          <div class="sidebar-toggle d-block d-lg-none mr-1">
            <feather-icon
              icon="MenuIcon"
              class="cursor-pointer"
              size="21"
              @click="$emit('show-sidebar')"
            />
          </div>
          <b-avatar
            size="36"
            variant="light-primary"
            class="mr-1 flex-shrink-0"
            :text="avatarText"
          />
          <div class="overflow-hidden">
            <h6 class="mb-0 text-truncate">
              {{ customerName }}
              <b-badge
                v-if="statusLabel"
                pill
                variant="light-secondary"
                class="ml-50"
              >
                {{ statusLabel }}
              </b-badge>
            </h6>
            <small
              class="text-muted d-block text-truncate"
              :title="peopleTitle"
            >
              {{ ownerText }}<template v-if="invitedText"> · Participantes: {{ invitedText }}</template>
            </small>
          </div>
        </div>

        <div class="d-flex align-items-center flex-shrink-0">
          <b-badge
            v-if="connectionLabel"
            :variant="connectionVariant"
            class="mr-1"
          >
            {{ connectionLabel }}
          </b-badge>
          <!-- Participantes e "Direcionar" (CustomerChat.vue → ConversationParticipants). -->
          <slot
            name="actions"
            :conversation="conversation"
          />
        </div>
      </header>
    </div>

    <!-- Timeline -->
    <div
      ref="scroller"
      class="user-chats customer-chat-scroller"
      @scroll="onScroll"
    >
      <div
        v-if="messagesState.loadingOlder"
        class="text-center mb-1"
      >
        <b-spinner
          small
          variant="primary"
        />
      </div>
      <p
        v-else-if="messagesState.loaded && !messagesState.hasMoreOlder"
        class="customer-chat-system"
      >
        <span class="customer-chat-system-text">{{ timelineStartText }}</span>
      </p>

      <div
        v-if="messagesState.loading && !messagesState.loaded"
        class="d-flex justify-content-center p-2"
      >
        <b-spinner variant="primary" />
      </div>
      <p
        v-else-if="messagesState.error && !messages.length"
        class="text-center text-muted"
      >
        Não foi possível carregar as mensagens.
      </p>

      <div class="chats">
        <chat-message-item
          v-for="message in messages"
          :key="message.id || message.clientMessageId"
          :message="message"
          :my-principal="myPrincipal"
          :customer-read-seq="messagesState.customerReadSeq || 0"
          @retry="retry"
          @discard="discard"
        />
      </div>
    </div>

    <div class="customer-chat-typing text-muted">
      <small v-if="typingName">{{ typingName }} está digitando…</small>
    </div>

    <!-- Composer -->
    <chat-composer
      v-if="canCompose"
      ref="composer"
      :conversation-id="conversation.conversationId"
      @sent="scrollToBottom"
    />
    <div
      v-else
      class="chat-app-form customer-chat-composer justify-content-center text-muted"
    >
      <small>{{ readOnlyReason }}</small>
    </div>
  </div>
</template>

<script>
import { BAvatar, BBadge, BSpinner } from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import { CUSTOMER_CHAT_RESOURCE, CUSTOMER_CHAT_WRITE_ACTION } from '@/constants/resources'
import * as types from '../store/types'
import ChatMessageItem from './ChatMessageItem.vue'
import ChatComposer from './ChatComposer.vue'
import { participantLabel } from '../utils/messages'
import { initials } from '../utils/format'
import { SOCKET_STATUS_CONNECTING, SOCKET_STATUS_RECONNECTING, SOCKET_STATUS_STOPPED } from '../services/chatSocket'

const LOAD_OLDER_THRESHOLD_PX = 60
const STICK_TO_BOTTOM_PX = 120

const STATUS_LABELS = {
  ARCHIVED: 'Arquivada',
  BLOCKED: 'Bloqueada',
}

export default {
  components: {
    BAvatar,
    BBadge,
    BSpinner,
    ChatMessageItem,
    ChatComposer,
  },
  props: {
    conversation: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      messages: types.OPEN_MESSAGES,
      messagesState: types.OPEN_MESSAGES_STATE,
      typingName: types.OPEN_TYPING,
      myPrincipal: types.MY_PRINCIPAL,
      connectionStatus: types.CONNECTION_STATUS,
    }),
    customerName() {
      return this.conversation.customerDisplayName || 'Cliente'
    },
    avatarText() {
      return initials(this.customerName)
    },
    statusLabel() {
      return STATUS_LABELS[this.conversation.status] || null
    },
    ownerText() {
      return this.conversation.owner
        ? `Responsável: ${participantLabel(this.conversation.owner)}`
        : 'Sem responsável no momento'
    },
    invitedText() {
      return (this.conversation.participants || [])
        .filter(participant => participant.role === 'INVITED')
        .map(participantLabel)
        .filter(Boolean)
        .join(', ')
    },
    peopleTitle() {
      return this.invitedText ? `${this.ownerText} · Participantes: ${this.invitedText}` : this.ownerText
    },
    canWrite() {
      return this.$can(CUSTOMER_CHAT_WRITE_ACTION, CUSTOMER_CHAT_RESOURCE)
    },
    canCompose() {
      return this.conversation.canSend === true && this.canWrite
    },
    readOnlyReason() {
      if (!this.canWrite) return 'Seu perfil tem acesso somente leitura ao chat.'
      if (this.conversation.status === 'BLOCKED') return 'Esta conversa está bloqueada.'
      if (this.conversation.status === 'ARCHIVED') return 'Esta conversa está arquivada.'
      return 'Você não pode enviar mensagens nesta conversa.'
    },
    timelineStartText() {
      return Number(this.conversation.readFromSeq) > 0
        ? 'Você vê a conversa a partir do momento em que foi incluído(a).'
        : 'Início da conversa'
    },
    lastMessageKey() {
      const last = this.messages[this.messages.length - 1]
      return last ? last.id || last.clientMessageId : null
    },
    connectionLabel() {
      if (this.connectionStatus === SOCKET_STATUS_CONNECTING) return 'Conectando…'
      if (this.connectionStatus === SOCKET_STATUS_RECONNECTING) return 'Reconectando…'
      if (this.connectionStatus === SOCKET_STATUS_STOPPED) return 'Offline'
      return null
    },
    connectionVariant() {
      return this.connectionStatus === SOCKET_STATUS_STOPPED ? 'light-danger' : 'light-warning'
    },
  },
  watch: {
    'conversation.conversationId': function onConversationChange() {
      this.$nextTick(this.scrollToBottom)
    },
    lastMessageKey(newKey, oldKey) {
      if (!newKey || newKey === oldKey) return
      const last = this.messages[this.messages.length - 1]
      const own = last && !!last.localStatus
      if (own || this.wasNearBottom || !oldKey) this.$nextTick(this.scrollToBottom)
    },
  },
  created() {
    // Não reativo de propósito: só decide se a timeline acompanha mensagens novas.
    this.wasNearBottom = true
  },
  mounted() {
    this.$nextTick(this.scrollToBottom)
  },
  methods: {
    scrollToBottom() {
      const el = this.$refs.scroller
      if (el) el.scrollTop = el.scrollHeight
      this.wasNearBottom = true
      // Poucas mensagens (sem barra de rolagem): não haverá evento de scroll para paginar.
      if (el && el.scrollHeight <= el.clientHeight) this.onScroll()
    },
    async onScroll() {
      const el = this.$refs.scroller
      if (!el) return
      this.wasNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_TO_BOTTOM_PX
      if (el.scrollTop > LOAD_OLDER_THRESHOLD_PX) return
      if (!this.messagesState.hasMoreOlder || this.messagesState.loadingOlder) return
      const previousHeight = el.scrollHeight
      const previousTop = el.scrollTop
      try {
        const added = await this.$store.dispatch(types.LOAD_OLDER_MESSAGES)
        if (!added) return
        // Mantém a posição de leitura depois de inserir mensagens acima.
        this.$nextTick(() => {
          el.scrollTop = el.scrollHeight - previousHeight + previousTop
        })
      } catch (e) {
        // falha silenciosa; nova rolagem tenta de novo
      }
    },
    retry(message) {
      this.$store.dispatch(types.RETRY_MESSAGE, {
        conversationId: this.conversation.conversationId,
        clientMessageId: message.clientMessageId,
      })
    },
    discard(message) {
      this.$store.dispatch(types.DISCARD_MESSAGE, {
        conversationId: this.conversation.conversationId,
        clientMessageId: message.clientMessageId,
      })
    },
  },
}
</script>
